/**
 * Perguntas frequentes em linguagem simples para usuários do dia a dia.
 * Itens podem ter `segments` (só aparecem nesses) ou `excludeSegments`.
 */
import { WorkspaceSegments } from "../../constants/workspaceSegments.js";
import { faqItemMatchesSegment } from "./helpSegment.js";

export const faqCategories = [
  {
    key: "getting_started",
    title: "Primeiros passos",
    items: [
      {
        q: "O que é o Xpro4?",
        a: "É o sistema onde sua equipe atende clientes, acompanha conversas, vendas e resultados — tudo em um só lugar. Você pode receber mensagens do WhatsApp, e-mail, site e outros meios de contato, responder, organizar o trabalho e ver como está indo o atendimento.",
      },
      {
        q: "Por onde começo depois de entrar no sistema?",
        a: "Um bom caminho é: (1) pedir a um responsável para conectar os canais de atendimento (WhatsApp, e-mail etc.); (2) ir em Atendimento e conhecer as conversas; (3) abrir o Dashboard (painel de números) para ver os resultados do período. Na Central de Ajuda, use \"Comece por aqui\" para ir direto às telas mais usadas.",
      },
      {
        q: "Por que não vejo alguns itens no menu?",
        a: "Cada pessoa vê apenas o que precisa para o seu trabalho. Se falta algo importante para você — por exemplo, Clientes ou Configurações — fale com o administrador da sua empresa para revisar seu perfil de acesso.",
      },
      {
        q: "Como altero meu nome ou foto?",
        a: "Clique na sua foto no canto superior direito e escolha Meu perfil. Lá você pode atualizar seus dados pessoais.",
      },
    ],
  },
  {
    key: "help_and_training",
    title: "Ajuda e treinamento",
    items: [
      {
        q: "Onde encontro ajuda?",
        a: "Clique na sua foto (canto superior direito) e escolha Ajuda e Treinamento. Lá você encontra materiais da sua empresa, o tour que mostra a tela passo a passo, e o botão Guia completo, que abre esta Central de Ajuda com tudo explicado.",
      },
      {
        q: "Qual a diferença entre o tour e o guia?",
        a: "O tour guiado aponta na própria tela onde clicar e o que significa cada parte — ideal para quem está começando. O guia é como um manual: você busca por assunto (pagamento, clientes, WhatsApp…) e lê explicações completas quando quiser.",
      },
      {
        q: "Como sei o que mudou na plataforma?",
        a: "Nesta Central de Ajuda, abra a aba O que há de novo. Lá aparecem as melhorias e correções de cada atualização. O número da versão que você usa também aparece no rodapé do menu lateral.",
      },
      {
        q: "Minha empresa pode colocar manuais e vídeos de treinamento?",
        a: "Sim. Quem administra o sistema pode publicar PDFs, vídeos e links em Conteúdos, na categoria de treinamento. Depois disso, o material fica disponível em Ajuda e Treinamento para quem a empresa autorizar.",
      },
    ],
  },
  {
    key: "chat",
    title: "Atendimento",
    items: [
      {
        q: "O que é uma conversa ou solicitação?",
        a: "É cada atendimento em andamento: uma mensagem no WhatsApp, um e-mail, um contato pelo site ou outro canal. Dentro dela você vê o histórico com o cliente, quem está responsável e em que situação está (aguardando, em atendimento, finalizada etc.).",
      },
      {
        q: "Como pego uma conversa para responder ou passo para outra pessoa?",
        a: "Abra a conversa em Atendimento. No topo, use o seletor \"Responsável\" para assumir você mesmo ou encaminhar para um colega, fila ou atendimento automático — conforme sua empresa configurou. Para ver todas as opções de transferência, consulte a pergunta seguinte sobre transferência detalhada.",
      },
      {
        q: "Para que serve classificar o tipo de atendimento?",
        a: "Ajuda a organizar e encontrar conversas depois — por exemplo, \"venda\", \"suporte\" ou \"cancelamento\". Isso também facilita relatórios e respostas automáticas. A configuração fica em Configurações → Tipos, geralmente feita pelo administrador.",
      },
      {
        q: "O que é \"Meu dia\"?",
        a: "É um atalho na lista do Atendimento que mostra só as suas solicitações pendentes ou em atendimento — para focar no que precisa de ação agora. É o filtro padrão ao entrar na tela.",
      },
      {
        q: "Como transfiro uma conversa para outra pessoa, chatbot ou agente de IA?",
        a: "No topo da conversa ou na lista, abra o seletor \"Responsável\" e escolha: Fila (aguardando alguém), um colega, um chatbot (atendimento automático) ou um agente de IA. Também dá para digitar @nome na área de mensagem — ao enviar, confirme se quer transferir e se a mensagem vai ao cliente. Agente de IA e pessoa humana não ficam ativos juntos como responsável principal.",
      },
      {
        q: "O que significa cada status da solicitação?",
        a: "Pendente: aguardando ação. Em atendimento: alguém está trabalhando nela. Contato futuro: agendada para retorno (com data de próximo contato). Concluída, Perdida e Cancelada: encerram a conversa — a área de mensagem some e você pode Reabrir ou criar Nova solicitação.",
      },
      {
        q: "Como funciona a janela de 24h do WhatsApp?",
        a: "No WhatsApp oficial (Meta), depois da última mensagem do cliente você tem 24 horas para responder livremente. Um ícone de relógio no topo da conversa avisa quando o prazo está acabando. Fora desse prazo (ou no primeiro contato iniciado pela empresa), escolha um modelo de mensagem de reengajamento na área de mensagem antes de enviar.",
      },
      {
        q: "Qual a diferença entre responder cliente e anotação interna?",
        a: "Responder cliente envia pelo canal escolhido (WhatsApp, e-mail etc.) e pode exigir dados do cliente quando o canal pede (por exemplo, telefone). Anotação interna fica só para a equipe, sem ir ao contato — útil para combinar algo entre colegas.",
      },
      {
        q: "Por que não consigo encerrar uma solicitação?",
        a: "Pode haver tarefas obrigatórias abertas (veja o painel Tarefas à direita) ou uma regra de automação bloqueando a mudança de status. O sistema mostra um aviso explicando o motivo.",
      },
      {
        q: "Como fixo uma solicitação no topo da lista?",
        a: "Clique com o botão direito na linha da solicitação e escolha Fixar no topo. Solicitações fixadas automaticamente pelo canal também aparecem no topo — passe o mouse sobre o ícone de fixação para ver a origem. Para remover qualquer fixação (incluindo a do canal), use Desafixar no mesmo menu.",
      },
      {
        q: "O que é busca avançada no Atendimento?",
        a: "No menu de filtros da lista, Busca avançada permite filtrar por tipo, subtipos, canal, responsável, etiquetas, datas, texto na mensagem e outros critérios. Ver em tabela abre a mesma seleção para ações em massa.",
      },
      {
        q: "Como unifico ou exporto solicitações de um cliente?",
        a: "Na ficha do cliente, aba Solicitações, marque os protocolos e use Unificar, Distribuir, Alterar ou Exportar — as mesmas ações da tela de resultados da busca. Unificar pede pelo menos duas solicitações; as mensagens vão para o protocolo principal e os outros são cancelados.",
      },
      {
        q: "Para que serve o painel de detalhes à direita?",
        a: "Mostra dados do Cliente, detalhes da Solicitação (tipo, canal, responsável), conversa da Equipe, Tarefas, assistente Tirar dúvidas (materiais do tipo de atendimento), resumo automático com IA, Produtos ou Passagens (conforme o tipo de operação) e histórico de outras solicitações do mesmo cliente.",
      },
    ],
  },
  {
    key: "automation",
    title: "Respostas automáticas e assistentes",
    items: [
      {
        q: "Qual a diferença entre chatbot e assistente com inteligência artificial?",
        a: "O chatbot segue um roteiro fixo — menus, botões e respostas que alguém montou antes (por exemplo: \"1 - Horário\", \"2 - Preços\"). O assistente com IA entende perguntas escritas livremente e responde com base em documentos e orientações que a empresa cadastrou — como um FAQ inteligente ou uma recepcionista virtual.",
      },
      {
        q: "O que são regras automáticas?",
        a: "São combinações do tipo \"quando acontecer X, fazer Y\" — por exemplo, avisar um supervisor quando uma conversa fica parada, ou mudar o status quando o cliente confirma um pedido. Quem administra o sistema configura isso em Regras de automação.",
      },
      {
        q: "Por que o assistente às vezes responde errado?",
        a: "Ele usa os documentos e links que a empresa cadastrou. Se o material estiver desatualizado ou incompleto, a resposta pode ficar imprecisa. Peça ao responsável para revisar os arquivos e perguntas frequentes vinculados ao assistente.",
      },
      {
        q: "Como transferir uma solicitação de um agente de IA para um chatbot?",
        a: "1) Abra Configurações → Agentes de IA e edite o agente. 2) Na função do agente (FAQ, recepcionista etc.), abra Ciclo de vida. 3) Ative a fase Escalonamento ou Falha. 4) Em Transferir para, escolha Chatbot e selecione o chatbot desejado. 5) Salve e teste enviando uma mensagem que dispare a regra. Para o caminho inverso (chatbot chamando agente de IA), edite o passo do chatbot em Configurações → Chatbots e use o modo de resposta Agente de IA.",
      },
    ],
  },
  {
    key: "channels_commerce",
    title: "Canais, produtos e pagamentos",
    items: [
      {
        q: "Como minha empresa conecta o WhatsApp ou outro canal?",
        a: "Quem administra acessa Configurações → Canais, escolhe o meio de contato (WhatsApp oficial Meta, WhatsApp por QR Code, Instagram, e-mail, SMS, telefonia etc.) e segue as instruções na tela. Depois configure Roteamento padrão (agente de IA, chatbot ou equipe humana) e Tipo de Atendimento. Teste enviando e recebendo uma mensagem de verdade.",
      },
      {
        q: "Como faço para o WhatsApp (ou outro canal) atender com agente de IA?",
        a: "Primeiro crie o agente em Configurações → Agentes de IA (função, documentos e regras). Depois em Configurações → Canais abra ou crie o canal (ex.: WhatsApp), conclua a conexão e em Roteamento padrão escolha Passar por agente de IA e selecione o agente. Salve, defina Tipo de Atendimento e teste enviando mensagem pelo canal.",
      },
      {
        q: "Qual a diferença entre rotear um canal para chatbot, agente de IA ou humano?",
        a: "No cadastro do canal, Roteamento padrão aceita uma opção por vez: equipe humana (fila ou responsável fixo), chatbot (menus e fluxo automático) ou agente de IA (respostas com base em documentos cadastrados). Novas solicitações daquele canal seguem essa escolha até alguém transferir manualmente ou uma regra do agente/chatbot encaminhar.",
      },
      {
        q: "Onde ficam pagamento e entrega?",
        a: "Em Configurações → Formas de pagamento e Formas de entrega. Lá a empresa define PIX, cartão, boleto, frete, retirada na loja e similares — conforme o tipo de negócio.",
      },
      {
        q: "Onde cadastro produtos, cupons e categorias?",
        a: "Produtos ficam no menu principal. Cupons, categorias e banners de vitrine ficam nas Configurações correspondentes. Se você só atende clientes, pode não precisar dessas telas — elas são mais usadas por quem cuida da loja ou do catálogo.",
        excludeSegments: [WorkspaceSegments.PassengerTransport],
      },
      {
        q: "Onde cadastro trechos, cidades e regras de passagem?",
        a: "Trechos ficam no menu principal (no transporte rodoviário aparecem como Trechos). Cidades atendidas ficam em Configurações → Cidades. Integrações de passagem ficam em Configurações → Integrações — geralmente configuradas pelo administrador.",
        segments: [WorkspaceSegments.PassengerTransport],
      },
      {
        q: "Como vendo passagem durante um atendimento?",
        a: "Abra a conversa em Atendimento. Quando sua empresa usa venda de passagens, pode aparecer opção para consultar viagens, escolher assento e concluir a venda sem sair da conversa. Se não vir essa opção, peça ao administrador para revisar integrações e permissões.",
        segments: [WorkspaceSegments.PassengerTransport],
      },
    ],
  },
  {
    key: "team_dashboard",
    title: "Equipe, números e relatórios",
    items: [
      {
        q: "Onde vejo minhas tarefas e a agenda da equipe?",
        a: "No menu Equipe (quando sua empresa usa essa função) ou em Minhas tarefas, no menu da sua foto. Lá você acompanha pendências, compromissos e o que precisa fazer com colegas.",
      },
      {
        q: "Como uso o Dashboard?",
        a: "O Dashboard é o painel de números e gráficos da sua operação. Escolha o período (datas) no topo e navegue pelas abas — Visão geral, Clientes, Vendas e Pesquisas. Se sua empresa vende produtos, use a aba Produtos; no transporte rodoviário, a aba Passagens mostra bilhetes vendidos, rotas, origens e destinos. Os cards resumem o período — clique nos números quando quiser ver mais detalhes.",
      },
      {
        q: "Posso exportar os números do Dashboard?",
        a: "Em muitas telas há opção de exportar para planilha ou PDF. Se não aparecer para você, pode ser que sua função não inclua essa ação — confirme com o administrador.",
      },
    ],
  },
  {
    key: "billing_admin",
    title: "Plano, cobrança e administração",
    audience: "billing",
    items: [
      {
        q: "Onde vejo o plano e a cobrança da empresa?",
        a: "No menu da sua foto → Plano e faturamento (visível para quem tem permissão). Lá você consulta consumo, valores e opções para ampliar ou mudar de plano.",
        audience: "billing",
      },
      {
        q: "Quem pode convidar novas pessoas para o sistema?",
        a: "Normalmente administradores ou gestores, em Configurações → Usuários. É importante dar acesso só ao que cada pessoa realmente precisa.",
        requiredPermission: ["users", "view"],
      },
    ],
  },
  {
    key: "platform_console",
    title: "Console da plataforma",
    audience: "platform",
    items: [
      {
        q: "O que é o Console da plataforma?",
        a: "É a área administrativa da Xpro para suporte avançado: gerenciar contas de empresas clientes, catálogo, governança e outras ferramentas internas. Usuários do dia a dia da empresa normalmente não precisam acessá-la.",
        audience: "platform",
      },
      {
        q: "Quem pode abrir o Console da plataforma?",
        a: "Somente perfis autorizados pela Xpro — em geral administradores principais da plataforma ou equipe interna. O atalho aparece no menu da sua foto quando você tem esse acesso.",
        audience: "platform",
      },
    ],
  },
  {
    key: "partner_portal",
    title: "Portal do parceiro",
    audience: "partner",
    items: [
      {
        q: "O que é o Portal do parceiro?",
        a: "Área para representantes e revendas acompanharem empresas clientes vinculadas, presença da equipe, precificação e cadastro de novas empresas.",
        audience: "partner",
      },
      {
        q: "Como acesso o Portal do parceiro?",
        a: "Entre com seu usuário de parceiro. O sistema abre direto no painel do representante, ou use o atalho no menu do perfil quando disponível.",
        audience: "partner",
      },
    ],
  },
];

export function faqCategoriesForSegment(segment) {
  return faqCategories
    .map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => faqItemMatchesSegment(item, segment)),
    }))
    .filter((cat) => cat.items.length > 0);
}
